import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import { MailOutline } from "@mui/icons-material";
import testimonialsData from "./testimonials.json";

const clientLogos = [
  { src: "/images/logo_yamaha.png", alt: "Yamaha", scale: 0.82 },
  { src: "/images/logo_gresini.png", alt: "Gresini", scale: 0.82 },
  { src: "/images/logo_jaguar.png", alt: "Jaguar", scale: 0.86, tintBlack: true },
  { src: "/images/logo_vr46.png", alt: "VR46", scale: 1.02 },
  { src: "/images/logo_bmw.png", alt: "BMW", scale: 0.88 },
  { src: "/images/logo_mlav.png", alt: "MLAV", scale: 0.94 },
  { src: "/images/logo_sporttravel.png", alt: "Sport Travel", scale: 0.9 }
];

export default function App() {
  const classes = useStyles();
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const testimonialRotationSpeed = 15000;
  const testimonialFadeSpeed = 500;

  useEffect(() => {
    // Rotate testimonials every 15 seconds
    const interval = setInterval(() => {
      handleNextTestimonial();
    }, testimonialRotationSpeed);
  
    // Clear the interval when currentTestimonialIndex changes
    return () => clearInterval(interval);
  }, [currentTestimonialIndex]); // Add currentTestimonialIndex as a dependency
  

  const handleNextTestimonial = () => {
    setIsFading(true); // Trigger fade out
    setTimeout(() => {
      setCurrentTestimonialIndex((prevIndex) => (prevIndex + 1) % testimonialsData.length);
      setIsFading(false); // Trigger fade in
    }, testimonialFadeSpeed); // Match fade-out duration
  };

  const handleCircleClick = (index) => {
    setIsFading(true); // Trigger fade out
    setTimeout(() => {
      setCurrentTestimonialIndex(index); // This triggers the interval reset
      setIsFading(false); // Trigger fade in
    }, testimonialFadeSpeed); // Match fade-out duration
  };

  const currentTestimonial = testimonialsData[currentTestimonialIndex];

  return (
    <>
      <div className={classes.hero}>
        <div className={classes.logoContainer}>
          <img src='/images/logo.png' className={classes.logo} alt="Logo" />
        </div>
        <div className={classes.tagline}>
          
        </div>
      </div>
      <div className={classes.mission}>
        Enterprise Technology for Race Team Operations
      </div>
      <div className={classes.clients}>
        {clientLogos.map((logo) => (
          <div className={classes.clientLogoContainer} key={logo.src}>
            <div className={classes.clientLogoFrame}>
              <img
                src={logo.src}
                className={classes.clientLogo}
                alt={logo.alt}
                style={{
                  transform: `scale(${logo.scale})`,
                  filter: logo.tintBlack ? "brightness(0) saturate(100%)" : "none"
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={classes.testimonials}>
        <div className={`${classes.quote} ${isFading ? classes.fadeOut : classes.fadeIn}`}>
          {currentTestimonial.quote}
        </div>
        <div className={`${classes.quotePerson} ${isFading ? classes.fadeOut : classes.fadeIn}`}>
          {currentTestimonial.person}
        </div>
        <div className={`${classes.quoteTeam} ${isFading ? classes.fadeOut : classes.fadeIn}`}>
          {currentTestimonial.team}
        </div>
        <div className={classes.circles}>
          {testimonialsData.map((_, index) => (
            <div
              key={index}
              className={`${classes.circle} ${currentTestimonialIndex === index ? classes.activeCircle : ""}`}
              onClick={() => handleCircleClick(index)}
            />
          ))}
        </div>
      </div>
      <div className={classes.footer}>
        <div className={classes.footerLeft}>
          <MailOutline style={{ marginRight: 5 }} />
          info@experienceinnovation.co.uk
        </div>
        <div className={classes.footerRight}>
          <a href='privacy.php'>Privacy Policy</a>
          <div className={classes.footerRightSpacing} />
          Copyright &copy; 2025 Experience Innovation Ltd.
        </div>
      </div>
    </>
  );
}

const useStyles = makeStyles(() => ({
  hero: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    background: "url(/images/hero.jpg) center center",
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    width: '100vw',
    height: '90vh',
    minHeight: 600
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: 'absolute',
    top: 0,
    width: '30%',
    maxWidth: 250,
    height: '12vh',
    maxHeight: 100,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: '#ffffff',
    '@media (max-width: 1024px)': {
      maxHeight: 90
    }
  },
  logo: {
    width: '90%',
    height: 'auto',
    position: 'absolute',
    bottom: 20
  },
  tagline: {
    textAlign: 'center',
    fontSize: '4vw',
    color: '#fff',
    lineHeight: 1.4,
    maxWidth: '80%',
    fontWeight: 'bold',
    '@media (max-width: 1024px)': {
      fontSize: '12.23vw'
    }
  },
  mission: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    textAlign: 'center',
    width: '60vw',
    paddingLeft: '20vw',
    paddingRight: '20vw',
    paddingTop: '15vh',
    paddingBottom: '15vh',
    fontSize: '2.5vw',
    lineHeight: 1.5,
    fontWeight: 200,
    '@media (max-width: 1024px)': {
      fontSize: '10.5vw',
      paddingTop: '20vh',
      paddingBottom: '22vh',
      width: '84vw',
      paddingLeft: '8vw',
      paddingRight: '8vw'
    }
  },
  clients: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    columnGap: '2vw',
    rowGap: '3vh',
    paddingLeft: '3vw',
    paddingRight: '3vw',
    '@media (max-width: 1024px)': {
      flexWrap: 'wrap'
    }
  },
  clientLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 120,
    flex: '0 1 calc((100% - 12vw) / 7)',
    '@media (max-width: 1024px)': {
      flex: '0 1 calc((100% - 4vw) / 3)'
    },
    '@media (max-width: 900px)': {
      flex: '0 1 calc((100% - 2vw) / 2)'
    },
    '@media (max-width: 560px)': {
      flex: '0 1 100%',
      minHeight: 100
    }
  },
  clientLogoFrame: {
    width: '100%',
    maxWidth: 200,
    height: 100,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  clientLogo:{
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    transformOrigin: 'center center'
  },
  testimonials: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    flexDirection: 'column',
    paddingTop: '15vh',
    paddingBottom: '15vh',
    paddingRight: '20vw',
    paddingLeft: '20vw',
    '@media (max-width: 1024px)': {
      paddingRight: '10vw',
      paddingLeft: '10vw'
    }
  },
  quote: {
    fontStyle: 'italic',
    marginBottom: '3vh',
    fontSize: '1.5vw',
    display: 'flex',
    '@media (max-width: 1024px)': {
      fontSize: '5vw',
      textAlign: 'center'
    }
  },
  quotePerson: {
    marginBottom: 5,
    fontSize: '1.25vw',
    '@media (max-width: 1024px)': {
      fontSize: '4vw',
      textAlign: 'center'
    }
  },
  quoteTeam: {
    fontSize: '1.25vw',
    '@media (max-width: 1024px)': {
      fontSize: '3vw',
      textAlign: 'center'
    }
  },
  fadeIn: {
    animation: `$fadeIn 1s ease-in`,
  },
  fadeOut: {
    animation: `$fadeOut 1s ease-out`,
  },
  circles: {
    display: "flex",
    gap: "10px",
    marginTop: '3vh'
  },
  circle: {
    width: "15px",
    height: "15px",
    borderRadius: "50%",
    backgroundColor: "#ccc",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  activeCircle: {
    backgroundColor: "#333",
  },
  footer: {
    display: 'flex',
    flex: 1,
    width: 'calc(100% - 80)',
    padding: 40,
    backgroundColor: '#00000010',
    flexDirection: 'row',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      width: 'calc(100% - 20)',
      padding: 10,
    }
  },
  footerLeft: {
    display: 'flex',
    flex: 1,
    alignItems: "center",
    '@media (max-width: 1024px)': {
      fontSize: '5vw',
      justifyContent: 'center',
      marginTop: 20,
      marginBottom: 20
    }
  },
  footerRight: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
    '@media (max-width: 1024px)': {
      fontSize: '3vw',
      flexDirection: 'column',
      justifyContent: 'center',
      marginBottom: 20
    }
  },
  footerRightSpacing: {
    width: 40,
    '@media (max-width: 1024px)': {
      width: 0,
      marginBottom: 20
    }
  },
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  "@keyframes fadeOut": {
    from: { opacity: 1 },
    to: { opacity: 0 },
  },
}));
